import { useEffect } from 'react';
import { useCloudId } from '@atlassian/jira-tenant-context-controller/src/components/cloud-id/index.tsx';
import { useTraits, useTraitsActions } from '@atlassian/jira-traits/src/TraitsStore.tsx';
import type { TraitsState } from '@atlassian/jira-traits/src/types.tsx';

const JSW_ACTIVATION_DATE_TRAIT = 'jira-software.ondemand.activationDate';
const TWO_WEEKS_IN_MS = 14 * 24 * 60 * 60 * 1000;

export const isTenantWithinFirstTwoWeeks = (site: TraitsState['SITE']): boolean => {
	if (!site.loaded) return false;
	const activationDate = site.attributes?.find((t) => t.name === JSW_ACTIVATION_DATE_TRAIT)?.value;
	if (!activationDate || typeof activationDate !== 'string') return false;

	const activation = new Date(activationDate);
	if (Number.isNaN(activation.getTime())) return false;

	const twoWeeksAfterActivation = new Date(activation.getTime() + TWO_WEEKS_IN_MS);
	return new Date() <= twoWeeksAfterActivation;
};

/**
 * Returns whether the tenant activated within the last 14 days.
 * Returns null while the SITE trait is loading so callers can defer committing to a
 * configuration until a definitive answer is available.
 */
export const useIsFirstTwoWeeks = (): boolean | null => {
	const cloudId = useCloudId();
	const { fetchTraits } = useTraitsActions();
	const [traitState] = useTraits();
	const isSiteLoaded = traitState.SITE.loaded;

	useEffect(() => {
		fetchTraits(['SITE'], { cloudId });
	}, [cloudId, fetchTraits]);

	if (!isSiteLoaded) return null;
	return isTenantWithinFirstTwoWeeks(traitState.SITE);
};
